import React, { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { TutorialProvider, useTutorial } from "@/contexts/TutorialContext";
import BottomNav from "@/components/layout/BottomNav";
import SideMenu from "@/components/layout/SideMenu";
import AuthPage from "@/pages/AuthPage";
import LanguageSelectPage from "@/pages/LanguageSelectPage";
import OnboardingPage from "@/pages/OnboardingPage";
import QuickStartPage from "@/pages/QuickStartPage";
import HomePage from "@/pages/HomePage";
import FridgePage from "@/pages/FridgePage";
import MealPlannerPage from "@/pages/MealPlannerPage";
import RecipePage from "@/pages/RecipePage";
import GroceryPage from "@/pages/GroceryPage";
import MyDishesPage from "@/pages/MyDishesPage";
import InsightsPage from "@/pages/InsightsPage";
import SettingsPage from "@/pages/SettingsPage";
import SubscriptionPage from "@/pages/SubscriptionPage";
import BillingSuccessPage from "@/pages/BillingSuccessPage";
import BillingCancelPage from "@/pages/BillingCancelPage";
import DailyTipsPage from "@/pages/DailyTipsPage";
import AdminPage from "@/pages/AdminPage";
import ErrorBoundary from "@/components/ErrorBoundary";
import { Menu } from "lucide-react";
import Landing from "@/pages/Landing";
import PrivacyPage from "@/pages/PrivacyPage";
import TermsPage from "@/pages/TermsPage";
import SupportPage from "@/pages/SupportPage";
import FAQPage from "@/pages/FAQPage";
import GuidePage from "@/pages/GuidePage";
import DemoPage from "@/pages/DemoPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import { LANDING_PATHS } from "@/lib/langRoutes";
import { reportLandingOnce } from "@/lib/utils";

// Every public address, and what to render at it. A page that is not an
// ordinary marketing page says so HERE, beside its route, rather than inside its
// own file: the kind of address something is belongs to the map of the site, not
// to the component, and a component that had to declare it would be a component
// that had to know why anyone was asking.
//
// Unmarked is the default, and the default is the one that counts. A new public
// page is one line in this table and it joins the funnel; nobody has to remember
// a call, and nobody has to have heard of the funnel at all. That is the whole
// change: what used to be two lines copied by hand into each page is now the
// absence of a marker here.
const UTILITY = "utility";    // reached with a purpose already in hand
const OWN_STEP = "own-step";  // reports a step of its own; a second would double-count

const PUBLIC_ROUTES = {
  "/privacy": [PrivacyPage, UTILITY],
  "/terms": [TermsPage, UTILITY],
  "/support": [SupportPage, UTILITY],
  "/forgot-password": [ForgotPasswordPage, UTILITY],
  "/reset-password": [ResetPasswordPage, UTILITY],
  // /demo sends demo_view. A landing_view from here as well would erase
  // direct_to_demo, which exists to catch a code pointed at the wrong place.
  "/demo": [DemoPage, OWN_STEP],
  "/faq": [FAQPage],
  "/guide": [GuidePage],
};

function AppRoutes() {
  const { user, loading } = useAuth();
  const { tutorialActive } = useTutorial();
  const [menuOpen, setMenuOpen] = useState(false);
  
  // Check if language has been selected (System Gate - before everything)
  const [languageSelected, setLanguageSelected] = useState(() => {
    return localStorage.getItem("mealpill_lang_selected") === "true";
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAF8] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#7B9E7F]/20 border-t-[#7B9E7F] rounded-full animate-spin" />
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════
  // SYSTEM GATE: Language Selection MUST be first - before Auth screens
  // User cannot proceed to Sign Up or Sign In until language is selected
  // ═══════════════════════════════════════════════════════════════════
  if (!languageSelected) {
    return (
      <LanguageSelectPage
        onComplete={() => {
          localStorage.setItem("mealpill_lang_selected", "true");
          setLanguageSelected(true);
        }}
      />
    );
  }

  // After language is selected, show Auth for unauthenticated users
  if (!user) {
    return (
      <Routes>
        <Route path="*" element={<AuthPage />} />
      </Routes>
    );
  }

  // Admin redirect — completely separate app interface
  if (user.is_admin) {
    return (
      <Routes>
        <Route path="*" element={<AdminPage />} />
      </Routes>
    );
  }

  // Show onboarding for authenticated users who haven't completed it
  if (!user.onboarding_completed) {
    return (
      <Routes>
        <Route path="*" element={<OnboardingPage />} />
      </Routes>
    );
  }

  // Quick Start: one-time gate after Tutorial completes/skips, before main app
  const qsUserId = user?.id;
  const tutorialDone = !!qsUserId && localStorage.getItem(`mealpill_tutorial_${qsUserId}_completed`) === "true";
  const quickStartDone = !!qsUserId && localStorage.getItem(`mealpill_quick_start_done_${qsUserId}`) === "true";
  if (!tutorialActive && tutorialDone && !quickStartDone) {
    return <QuickStartPage />;
  }

  return (
    <>
      <button
        data-testid="menu-toggle-button"
        onClick={() => setMenuOpen(true)}
        className="fixed top-6 left-5 z-[9999] w-10 h-10 rounded-full bg-white/80 backdrop-blur-sm border border-black/5 flex items-center justify-center shadow-sm hover:shadow-md transition-shadow"
      >
        <Menu className="w-5 h-5 text-[#3D4035]" strokeWidth={1.5} />
      </button>

      <SideMenu open={menuOpen} onClose={() => setMenuOpen(false)} />

      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/fridge" element={<FridgePage />} />
        <Route path="/planner" element={<MealPlannerPage />} />
        <Route path="/recipe/:id" element={<RecipePage />} />
        <Route path="/grocery" element={<GroceryPage />} />
        <Route path="/dishes" element={<MyDishesPage />} />
        <Route path="/tips" element={<DailyTipsPage />} />
        <Route path="/insights" element={<InsightsPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/subscription" element={<SubscriptionPage />} />
        <Route path="/billing/success" element={<BillingSuccessPage />} />
        <Route path="/billing/cancel" element={<BillingCancelPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

      <BottomNav />
    </>
  );
}

function AppShell() {
  const location = useLocation();
  const { user, loading } = useAuth();
  // Trailing slash stripped before anything is matched against it. Every check
  // below compares exact strings, so "/faq/" missed "/faq" and fell through to
  // the app - which for a first-time visitor meant the language gate instead of
  // the page they asked for. Links elsewhere on the web will carry that slash
  // whatever we do, so the match has to tolerate it.
  const path = location.pathname.replace(/\/+$/, "") || "/";

  const [PublicPage, kind] = PUBLIC_ROUTES[path] || [];
  const landingAddress =
    path === '/welcome' ||
    path === '/landing-preview' ||
    LANDING_PATHS.includes(path);
  // A marketing address is a public one that is nobody's second step.
  const marketing = (Boolean(PublicPage) && !kind) || landingAddress;
  const signedIn = !!user;

  // Top of the funnel: someone reached the site at all. It lives here, above
  // every page, because it is a fact about the visit and not about the page -
  // the page that happened to be first has no more claim to report it than the
  // second one does, and while each page reported for itself, a page that said
  // nothing was indistinguishable from a page nobody opened.
  //
  // Never for a signed-in visitor, on any address. They are not arriving, they
  // are ours already. /welcome and /landing-preview render the landing page to
  // customers too, and used to count each of them as a new arrival.
  //
  // Waits for loading, or a visitor whose session had not resolved yet would be
  // counted as anonymous, which is the same error one beat earlier.
  useEffect(() => {
    if (loading || signedIn || !marketing) return;
    reportLandingOnce();
  }, [loading, signedIn, marketing]);

 // Wait for auth state to resolve before deciding what to render
  // (prevents flash of landing page for logged-in users on root URL)
  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAF8] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#7B9E7F]/20 border-t-[#7B9E7F] rounded-full animate-spin" />
      </div>
    );
  }

  // Public pages — rendered outside the mobile-width App container
  if (PublicPage) return <PublicPage />;

  // Landing page renders for: explicit landing URLs (any user), OR a marketing
  // URL for non-authenticated users. /uk and /ru are the same page in another
  // language - a signed-in visitor who opens one gets the app, exactly as they
  // would at "/", so the addresses stay purely a marketing concern.
  const showLanding =
    path === '/welcome' ||
    path === '/landing-preview' ||
    (LANDING_PATHS.includes(path) && !user);

  if (showLanding) {
    return <Landing />;
  }

  return (
    <div className="App">
      <AppRoutes />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <LanguageProvider>
        <AuthProvider>
          <TutorialProvider>
            <ErrorBoundary>
              <AppShell />
            </ErrorBoundary>
          </TutorialProvider>
        </AuthProvider>
      </LanguageProvider>
    </BrowserRouter>
  );
}

export default App;
